import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useFeatures } from '../hooks/useFeatures';
import { usePaywall } from '../components/Paywall';
import { NudgeBanner, ReadinessBar } from '../components/Paywall';
import { TierBadge } from '../components/UpgradePrompt';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { StreakCard } from '../components/StreakCard';
import { 
  BookOpen, 
  Exam, 
  RoadHorizon, 
  CaretCircleLeft,
  TrendUp,
  CheckCircle,
  Target,
  Trophy,
  ArrowRight,
  Sparkle,
  Brain,
  Medal,
  Lock,
  Crown,
  Sword,
  CalendarBlank,
  Clock,
  Warning,
} from '@phosphor-icons/react';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DashboardPage = () => {
  const { user } = useAuth();
  const { tier, hasFeature } = useFeatures();
  const paywall = usePaywall();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [categories, setCategories] = useState([]);
  const [recentBadges, setRecentBadges] = useState([]);
  const [testDate, setTestDate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [progressRes, categoriesRes, badgesRes, testDateRes] = await Promise.all([
          axios.get(`${API}/progress`, { withCredentials: true }),
          axios.get(`${API}/questions/categories`, { withCredentials: true }),
          axios.get(`${API}/badges/recent?limit=4`, { withCredentials: true }),
          axios.get(`${API}/settings/test-date`, { withCredentials: true }).catch(() => ({ data: null })),
        ]);
        setProgress(progressRes.data);
        setCategories(categoriesRes.data);
        setRecentBadges(badgesRes.data);
        setTestDate(testDateRes.data);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  const accuracy = progress?.accuracy || 0;
  const totalAnswered = progress?.total_questions_answered || 0;
  const mocksTaken = progress?.mock_exams_taken || 0;
  const mocksPassed = progress?.mock_exams_passed || 0;

  return (
    <div className="min-h-screen bg-background p-4 md:p-8" data-testid="dashboard">
      {/* Header */}
      <div className="mb-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-2">
          Welcome back
        </p>
        <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tighter">
          {user?.name?.split(' ')[0] || 'Learner'}
        </h1>
      </div>

      {/* Test Date Countdown */}
      <TestDateCountdown testDate={testDate} readiness={paywall?.readiness?.readiness} tier={tier} />

      {/* Nudge Banner (dynamic paywall time-based nudge) */}
      {paywall?.nudge && (
        <div className="mb-8">
          <NudgeBanner
            nudge={paywall.nudge}
            onAction={() => {
              if (paywall.nudge.plan) {
                navigate('/subscription');
              } else {
                navigate('/practice');
              }
            }}
          />
        </div>
      )}

      {/* Test Readiness Progress Bar */}
      {paywall?.readiness && (
        <div className="mb-8 bg-card border border-border rounded-sm p-4" data-testid="readiness-section">
          <ReadinessBar
            readiness={paywall.readiness.readiness}
            isLocked={paywall.readiness.is_locked}
            lockThreshold={paywall.readiness.lock_threshold}
            milestone={paywall.readiness.milestone}
            breakdown={paywall.readiness.breakdown}
          />
          {paywall.readiness.is_locked && (
            <Button
              size="sm"
              className="rounded-sm mt-3 w-full"
              onClick={() => navigate('/subscription')}
              data-testid="readiness-upgrade-btn"
            >
              <Crown weight="fill" className="w-4 h-4 mr-2" />
              Upgrade to unlock full progress
            </Button>
          )}
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard 
          icon={CheckCircle} 
          label="Questions" 
          value={totalAnswered} 
          color="text-success"
        />
        <StatCard 
          icon={Target} 
          label="Accuracy" 
          value={`${accuracy}%`} 
          color="text-primary"
        />
        <StatCard 
          icon={Exam} 
          label="Mock Exams" 
          value={mocksTaken} 
          color="text-accent"
        />
        <StatCard 
          icon={Trophy} 
          label="Passed" 
          value={mocksPassed} 
          color="text-success"
        />
      </div>

      {/* Streak Card */}
      <div className="mb-8">
        <StreakCard />
      </div>

      {/* Recent Badges */}
      {recentBadges.length > 0 && (
        <div className="mb-8" data-testid="recent-badges">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-bold text-xl tracking-tight">Recent Badges</h2>
            <Link to="/achievements" className="text-primary text-sm font-medium hover:underline flex items-center gap-1">
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {recentBadges.map(badge => (
              <div key={badge.id} className="bg-card border border-accent/40 rounded-sm p-4 text-center">
                <Medal weight="fill" className="w-8 h-8 text-accent mx-auto mb-2" />
                <p className="font-heading font-bold text-sm">{badge.name}</p>
                <p className="text-xs text-muted-foreground">{badge.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
        <QuickActionCard
          to="/smart-review"
          icon={Brain}
          title="Smart Review"
          description="AI-powered spaced repetition"
          color="bg-violet-600"
          locked={!hasFeature('smart_review')}
          onLockedClick={() => paywall?.checkFeatureLock('smart_review')}
        />
        <QuickActionCard
          to="/practice"
          icon={BookOpen}
          title="Practice Quiz"
          description="Practice with random questions"
          color="bg-primary"
        />
        <QuickActionCard
          to="/mock-exam"
          icon={Exam}
          title="Mock Exam"
          description="Take a full 50-question test"
          color="bg-accent"
          locked={!hasFeature('mock_exam')}
          onLockedClick={() => paywall?.checkFeatureLock('mock_exam')}
        />
        <QuickActionCard
          to="/hazard-perception"
          icon={CaretCircleLeft}
          title="Hazard Perception"
          description="Learn hazard identification"
          color="bg-success"
          locked={!hasFeature('hazard_perception')}
          onLockedClick={() => paywall?.checkFeatureLock('hazard_perception')}
        />
        <QuickActionCard
          to="/challenge"
          icon={Sword}
          title="Challenge"
          description="Challenge a friend to a quiz"
          color="bg-amber-600"
        />
      </div>

      {/* Upgrade Banner (for non-premium users) */}
      {tier !== 'premium' && (
        <div className="bg-gradient-to-r from-accent/20 to-primary/20 border border-accent/30 rounded-sm p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-accent/20 rounded-sm flex items-center justify-center flex-shrink-0">
              <Crown weight="fill" className="w-6 h-6 text-accent" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading font-bold text-lg mb-1">
                {tier === 'free' ? 'Unlock All Features' : 'Pass Even Faster'}
              </h3>
              <p className="text-muted-foreground text-sm mb-3">
                {tier === 'free' 
                  ? 'Get access to all categories, mock exams, smart review, and more.'
                  : 'Unlock AI explanations and hazard perception practice.'}
              </p>
              <Link to="/subscription">
                <Button size="sm" className="rounded-sm" data-testid="upgrade-button">
                  View Plans
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Progress by Category */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-bold text-xl tracking-tight">Progress by Topic</h2>
          <Link to="/practice" className="text-primary text-sm font-medium hover:underline flex items-center gap-1">
            Practice All <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {categories.slice(0, 6).map((cat) => {
            const catProgress = progress?.categories_progress?.[cat.id];
            const answered = catProgress?.answered || 0;
            const correct = catProgress?.correct || 0;
            const catAccuracy = answered > 0 ? Math.round((correct / answered) * 100) : 0;
            
            return (
              <Link 
                key={cat.id} 
                to={`/practice?category=${cat.id}`}
                className="card-hover"
              >
                <div className="bg-card border border-border p-4 rounded-sm">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-medium text-sm">{cat.name}</span>
                    <span className="text-xs text-muted-foreground">{answered} answered</span>
                  </div>
                  <Progress value={catAccuracy} className="h-2 rounded-none" />
                  <p className="text-xs text-muted-foreground mt-2">{catAccuracy}% correct</p>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Recent Mock Exams */}
      {progress?.recent_exams?.length > 0 && (
        <div>
          <h2 className="font-heading font-bold text-xl tracking-tight mb-4">Recent Mock Exams</h2>
          <div className="space-y-2">
            {progress.recent_exams.map((exam, index) => (
              <div key={index} className="bg-card border border-border p-4 rounded-sm flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={cn(
                    "w-10 h-10 rounded-sm flex items-center justify-center",
                    exam.passed ? "bg-success/20 text-success" : "bg-destructive/20 text-destructive"
                  )}>
                    {exam.passed ? <CheckCircle weight="fill" className="w-5 h-5" /> : <TrendUp className="w-5 h-5" />}
                  </div>
                  <div>
                    <p className="font-medium text-sm">
                      {exam.score}/{exam.total} ({Math.round((exam.score/exam.total)*100)}%)
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(exam.timestamp).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <span className={cn(
                  "text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-sm",
                  exam.passed ? "bg-success/20 text-success" : "bg-destructive/20 text-destructive"
                )}>
                  {exam.passed ? 'PASSED' : 'FAILED'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-card border border-border p-4 rounded-sm">
    <div className="flex items-center gap-3">
      <Icon weight="fill" className={cn("w-8 h-8", color)} />
      <div>
        <p className="font-heading font-bold text-2xl tracking-tight">{value}</p>
        <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
      </div>
    </div>
  </div>
);

const QuickActionCard = ({ to, icon: Icon, title, description, color, locked, onLockedClick }) => {
  const handleClick = (e) => {
    if (locked && onLockedClick) {
      e.preventDefault();
      onLockedClick();
    }
  };

  return (
    <Link to={to} className="card-hover" data-testid={`action-${title.toLowerCase().replace(' ', '-')}`} onClick={handleClick}>
      <div className={cn("bg-card border border-border p-6 rounded-sm h-full relative", locked && "opacity-70")}>
        {locked && (
          <div className="absolute top-3 right-3">
            <Lock weight="fill" className="w-4 h-4 text-accent" />
          </div>
        )}
        <div className={cn("w-12 h-12 rounded-sm flex items-center justify-center mb-4", color)}>
          <Icon weight="fill" className="w-6 h-6 text-white" />
        </div>
        <h3 className="font-heading font-bold text-lg mb-1">{title}</h3>
        <p className="text-muted-foreground text-sm">
          {locked ? 'Upgrade to unlock' : description}
        </p>
      </div>
    </Link>
  );
};


const TestDateCountdown = ({ testDate, readiness, tier }) => {
  const navigate = useNavigate();

  // No test date set — show prompt to set one
  if (!testDate || !testDate.test_date) {
    return (
      <div className="mb-8 bg-card border border-dashed border-border rounded-sm p-4 flex items-center gap-4" data-testid="test-date-prompt">
        <div className="w-10 h-10 rounded-sm bg-muted flex items-center justify-center flex-shrink-0">
          <CalendarBlank weight="duotone" className="w-5 h-5 text-muted-foreground" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium">When is your theory test?</p>
          <p className="text-xs text-muted-foreground">Set your date to get a personalised study countdown</p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => navigate('/settings')}
          className="rounded-sm text-xs flex-shrink-0"
          data-testid="set-test-date-btn"
        >
          Set date
        </Button>
      </div>
    );
  }

  const { days_remaining, urgency } = testDate;

  // Test date has passed
  if (urgency === 'passed') {
    return (
      <div className="mb-8 bg-card border border-muted rounded-sm p-4 flex items-center gap-4" data-testid="test-date-passed">
        <div className="w-10 h-10 rounded-sm bg-muted flex items-center justify-center flex-shrink-0">
          <CheckCircle weight="fill" className="w-5 h-5 text-success" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium">Your test date has passed</p>
          <p className="text-xs text-muted-foreground">Update your date if you have a new booking</p>
        </div>
        <Button size="sm" variant="outline" onClick={() => navigate('/settings')} className="rounded-sm text-xs flex-shrink-0">
          Update
        </Button>
      </div>
    );
  }

  // Urgency styling
  const urgencyConfig = {
    critical: {
      border: 'border-destructive/50',
      bg: 'bg-destructive/5',
      numColor: 'text-destructive',
      labelColor: 'text-destructive',
      pulse: true,
    },
    high: {
      border: 'border-destructive/30',
      bg: 'bg-destructive/5',
      numColor: 'text-destructive',
      labelColor: 'text-destructive/80',
      pulse: false,
    },
    medium: {
      border: 'border-amber-500/30',
      bg: 'bg-amber-500/5',
      numColor: 'text-amber-500',
      labelColor: 'text-amber-500/80',
      pulse: false,
    },
    low: {
      border: 'border-blue-500/30',
      bg: 'bg-blue-500/5',
      numColor: 'text-blue-400',
      labelColor: 'text-blue-400/80',
      pulse: false,
    },
  };
  const config = urgencyConfig[urgency] || urgencyConfig.low;

  const readinessMsg = readiness != null
    ? urgency === 'critical' || urgency === 'high'
      ? `You're ${readiness}% ready — ${tier === 'free' ? 'upgrade to catch up' : 'keep practicing'}`
      : `You're ${readiness}% test ready`
    : null;

  return (
    <div className={cn('mb-8 border rounded-sm p-4', config.border, config.bg)} data-testid="test-date-countdown">
      <div className="flex items-center gap-4">
        <div className={cn('text-center min-w-[64px]', config.pulse && 'animate-pulse')}>
          <p className={cn('font-heading font-black text-4xl tracking-tighter leading-none', config.numColor)}>
            {days_remaining}
          </p>
          <p className={cn('text-[10px] font-bold uppercase tracking-wider', config.labelColor)}>
            {days_remaining === 1 ? 'day' : 'days'}
          </p>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <Clock weight="fill" className={cn('w-4 h-4', config.numColor)} />
            <p className="text-sm font-bold">
              {urgency === 'critical' ? 'Test is almost here!' :
               urgency === 'high' ? 'Your test is this week' :
               urgency === 'medium' ? 'Your test is coming up' :
               'Countdown to your test'}
            </p>
          </div>
          {readinessMsg && (
            <p className="text-xs text-muted-foreground">{readinessMsg}</p>
          )}
          <p className="text-[10px] text-muted-foreground/60 mt-0.5">
            {new Date(testDate.test_date + 'T00:00:00').toLocaleDateString('en-GB', {
              weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
            })}
          </p>
        </div>
        {tier === 'free' && (urgency === 'critical' || urgency === 'high') && (
          <Button
            size="sm"
            className="rounded-sm text-xs flex-shrink-0"
            onClick={() => navigate('/subscription')}
            data-testid="countdown-upgrade-btn"
          >
            <Crown weight="fill" className="w-3 h-3 mr-1" />
            Upgrade
          </Button>
        )}
      </div>
    </div>
  );
};
